import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { getUserInfo, getUserRole } from "../utils/auth";
import { formatRole, getInitials, getNavigation, isSectionActive } from "./navigation";

const STORAGE_KEY = "hecmis.sidebar.groups";

/**
 * Desktop sidebar (hidden below the `sm` breakpoint; the header's drawer
 * covers mobile navigation). It sticks below the fixed header using the shared
 * `--app-header-h` variable so it can never overlap the header, and works both
 * inside a flex row and inside the legacy fixed <aside> wrappers.
 */
const SidebarComponent = () => {
  const location = useLocation();
  const userRole = getUserRole();
  const userInfo = getUserInfo();
  const navigation = getNavigation(userRole);

  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      return typeof saved === "object" && saved ? saved : {};
    } catch {
      return {};
    }
  });

  // Always reveal the group that contains the current page
  useEffect(() => {
    const active = navigation.find((s) => !s.path && isSectionActive(s, location.pathname));
    if (active && openGroups[active.key] === false) {
      setOpenGroups((g) => ({ ...g, [active.key]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggle = (key) =>
    setOpenGroups((g) => {
      const next = { ...g, [key]: !(g[key] ?? true) };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });

  const displayName =
    [userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(" ") || userInfo?.email || "Signed in";

  return (
    <aside
      className="hidden sm:flex sm:sticky sm:top-[var(--app-header-h)] w-64 flex-shrink-0 h-[calc(100vh-var(--app-header-h))] flex-col bg-white border-r border-slate-200"
      aria-label="Sidebar"
    >
      {/* Who is signed in */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-2.5">
          <span className="inline-flex w-9 h-9 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-cyan-700 text-white text-xs font-bold flex-shrink-0">
            {getInitials(userInfo)}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
            <p className="text-[11px] font-medium uppercase tracking-wide text-cyan-700 truncate">{formatRole(userRole)}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-1" aria-label="Sidebar">
        {navigation.map((section) => {
          const Icon = section.icon;

          if (section.path) {
            const active = location.pathname === section.path;
            return (
              <Link
                key={section.key}
                to={section.path}
                className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                  active ? "bg-cyan-50 text-cyan-900" : "text-slate-700 hover:bg-slate-100 hover:text-cyan-900"
                }`}
              >
                {active && <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-cyan-600" />}
                <Icon className={`w-5 h-5 ${active ? "text-cyan-600" : "text-slate-400 group-hover:text-cyan-600"}`} />
                <span>{section.title}</span>
              </Link>
            );
          }

          const open = openGroups[section.key] ?? true;
          const sectionActive = isSectionActive(section, location.pathname);

          return (
            <div key={section.key} className="pt-2">
              <button
                type="button"
                onClick={() => toggle(section.key)}
                aria-expanded={open}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors hover:bg-slate-100"
              >
                <span className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${sectionActive ? "text-cyan-600" : "text-slate-400"}`} />
                  <span className={`text-xs font-semibold uppercase tracking-wider ${sectionActive ? "text-cyan-800" : "text-slate-500"}`}>
                    {section.title}
                  </span>
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
              </button>

              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${open ? "max-h-[1600px] opacity-100" : "max-h-0 opacity-0"}`}>
                <div className="mt-1 space-y-0.5">
                  {section.items.map((item) => {
                    const ItemIcon = item.icon;
                    const active = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`group relative flex items-start gap-3 pl-3 pr-2 py-2 rounded-lg text-sm transition-colors ${
                          active ? "bg-cyan-50 text-cyan-900" : "text-slate-600 hover:bg-slate-100 hover:text-cyan-900"
                        }`}
                      >
                        {active && <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-cyan-600" />}
                        <ItemIcon
                          className={`w-4 h-4 mt-0.5 flex-shrink-0 ${active ? "text-cyan-600" : "text-slate-400 group-hover:text-cyan-600"}`}
                        />
                        <span className="min-w-0">
                          <span className={`block leading-5 ${active ? "font-semibold" : "font-medium"}`}>{item.title}</span>
                          {item.description && (
                            <span className={`block text-[11px] leading-4 ${active ? "text-cyan-700/80" : "text-slate-400"}`}>
                              {item.description}
                            </span>
                          )}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="flex-shrink-0 px-4 py-3 border-t border-slate-200 bg-slate-50/60">
        <p className="text-xs font-semibold text-slate-700">HEC MIS</p>
        <p className="text-[11px] text-slate-500">Higher Education Council · Management System</p>
      </div>
    </aside>
  );
};

export default SidebarComponent;
